import React, { useCallback, useMemo, useState } from 'react';
import { Modal, ModalSize } from 'components/modal/Modal';
import { DealUIObjectWithBrand } from './DealsSection';
import { Btn } from 'components/btn/Btn';
import { colors } from 'styles/settings';
import { ExternalLinkArrow } from 'features/wallet/components/ShopOnlineBanner';
import { createStyles } from 'utils/createStyle';
import { useGetFlipgiveToken } from 'common/hooks/useGetAffiliateActivationUrl';
import { DealCouponButton } from './DealCouponButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faTimer } from '@fortawesome/pro-regular-svg-icons';
import AffiliateConfirmationModal from 'features/product/components/AffiliateConfirmationModal';
import { getConfirmationDrawerText } from 'common/utils/confirmationDrawer';
import { logFGDealClick } from 'config/analytics';
import { useRootSelector } from 'rootStore';
import { selectSelectedOrgId } from 'common/features/store/duck/home/duck';
import { useSelector } from 'react-redux';
import useMediaQueries from 'hooks/useMediaQueries';

export interface DealsModalProps {
    isOpen: boolean;
    onClose: () => void;
    dealWithBrand: DealUIObjectWithBrand;
    location?: 'homepage' | 'homepage_category_top_deals';
}

const dealsModalsStyles = createStyles({
    header: ['flex flex-col items-center w-full'],
    doubleDipInfo: [
        'mt-[16px] flex flex-row justify-around items-center w-full border border-warning-orange mb-[24px]',
        {
            background: '#FFFAEB',
        },
    ],
    doubleDipInfoIcon: ['text-warning-orange text-[14px] font-semibold pl-3 pr-[12px]'],
    doubleDipInfoText: [
        'text-warning-orange text-[14px] font-semibold pr-[8px] leading-[14px] py-[13px]',
    ],
    brandContainer: [
        'w-[160px] h-[112px] border border-grey-9 rounded-[12px] relative flex flex-col items-center mb-[6px] justify-around shadow-sm',
    ],
    bonusSpacer: ['h-[16px] self-stretch'],
    bonusBanner: [
        'text-center uppercase h-[20px] absolute left-0 bottom-0 right-0 text-[10px] font-bold bg-bonus-pink text-white tracking-[1px] leading-[22px] rounded-br-[12px] rounded-bl-[12px]',
    ],
    couponHelpText: ['text-[14px] leading-[20px] text-grey-darkest max-w-[320px]'],
    couponCopy: [
        'text-[14px] text-grey-darkest font-normal max-w-[400px] text-center mt-[17px] mb-[31px] leading-[20px]',
    ],
    expirationMessage: ['text-warning-orange text-[14px] font-semibold mt-[10px]'],
    expirationMessageIcon: ['text-warning-orange text-[14px] font-semibold pr-[4px]'],
    earningsText: ['text-[16px] text-grey-darkest'],
    strikethrough: ['line-through'],
    bonusValue: ['text-dark-blue text-[20px] font-semibold pl-1'],
    rebateValue: ['text-dark-blue text-[20px] font-semibold'],
    dealTitle: [
        'text-center text-[24px] font-semibold text-dark-blue pb-[20px] pt-[8px] leading-[34px]',
    ],
    externalLinkArrow: ['inline ml-2 w-3 h-5 mb-[2px]'],
    shopButton: ['mb-[32px]'],
    detailsSection: ['px-[24px] py-[28px] pb-[23px] bg-grey-accent mb-[12px]'],
    sectionTitle: ['text-dark-blue text-[16px] font-semibold pb-[23px]'],
    sectionSubTitle: ['font-semibold text-[14px] text-grey-darkest leading-[20px] mb-[20px]'],
    sectionContent: [
        'text-[14px] text-grey-darkest leading-[20px] mb-[20px]',
        {
            '& ul': {
                listStyleType: 'disc',
            },
        },
    ],
});

const useIsMobileOrDesktop = () => {
    const [type, setType] = useState<'mobile-web' | 'web'>('web');
    const queries = useMemo(
        () => ({
            '(max-width: 640px)': () => {
                setType('mobile-web');
            },
            '(min-width: 641px)': () => {
                setType('web');
            },
        }),
        []
    );
    useMediaQueries(queries);

    return type;
};

export const DealsModal = ({ isOpen, onClose, dealWithBrand, location }: DealsModalProps) => {
    const [confirmationModalShown, setConfirmationModalShown] = React.useState(false);
    const brand = dealWithBrand.brand;
    const { token, isLoading: isLoadingFlipgiveToken } = useGetFlipgiveToken();

    const [lastClickedCoupon, setLastClickedCoupon] = React.useState<DealUIObjectWithBrand | null>(
        null
    );
    const thisDealIsLastClicked = lastClickedCoupon && lastClickedCoupon.url === dealWithBrand.url;
    const orgId = useSelector(selectSelectedOrgId);

    const type: 'web' | 'mobile-web' = useIsMobileOrDesktop();

    const clickOfferLink = useCallback(() => {
        const link = dealWithBrand.url || brand?.affiliateOfferUrl;

        if (link) {
            if (setLastClickedCoupon) {
                setLastClickedCoupon(() => dealWithBrand);
            }

            const linkWithToken = link.replace('{{token}}', token).replace('{{web}}', 'true');

            const urlWithUTMs = new URL(linkWithToken);
            urlWithUTMs.searchParams.set('utm_channel', 'web');
            urlWithUTMs.searchParams.set('utm_source', 'homepage');
            urlWithUTMs.searchParams.set('utm_campaign', dealWithBrand.dealType || 'deal');

            setConfirmationModalShown(true);
            window.open(urlWithUTMs.toString(), '_blank', 'noopener,noreferrer');

            logFGDealClick({
                dealType: dealWithBrand.dealType || 'deal',
                brandId: String(brand?.id || 0),
                brandName: brand?.name || '',
                version: `react-web-${process.env.REACT_APP_VERSION}`,
                location: location || 'homepage',
                orgId: orgId,
                affiliateMobilePlatform: type,
            });
        }
    }, [
        brand?.id,
        brand?.affiliateOfferUrl,
        dealWithBrand.url,
        token,
        isLoadingFlipgiveToken,
        type,
    ]);

    return (
        <>
            <Modal
                title=""
                isOpen={isOpen && !confirmationModalShown}
                onClose={onClose}
                showCloseButton={true}
                size={[ModalSize.MEDIUM, ModalSize.MEDIUM]}
            >
                <article>
                    <div className={dealsModalsStyles.header}>
                        {!brand?.canDoubleDip && (
                            <div className={dealsModalsStyles.doubleDipInfo}>
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className={dealsModalsStyles.doubleDipInfoIcon}
                                />

                                <span className={dealsModalsStyles.doubleDipInfoText}>
                                    For this brand, both gift card and online earnings cannot be
                                    combined.
                                </span>
                            </div>
                        )}

                        <div className={dealsModalsStyles.brandContainer}>
                            <img src={brand?.imageUrl} />

                            {brand?.affiliateBonus && (
                                <>
                                    {/* spacer for flexbox */}
                                    <div className={dealsModalsStyles.bonusSpacer} />

                                    {/* visible bonus banner */}
                                    <div className={dealsModalsStyles.bonusBanner}>Bonus</div>
                                </>
                            )}
                        </div>

                        <div className="self-center">
                            <span className={dealsModalsStyles.earningsText}>
                                Earn
                                {brand && brand.affiliateExclusionText ? ' up to ' : ' '}
                                {brand && brand.affiliateBonus ? (
                                    <>
                                        <span className={dealsModalsStyles.strikethrough}>
                                            {brand && brand?.affiliateRebate}%
                                        </span>
                                        <span className={dealsModalsStyles.bonusValue}>
                                            {brand && brand?.affiliateBonus}% +
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className={dealsModalsStyles.rebateValue}>
                                            {brand && brand?.affiliateRebate}% +
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>

                        {dealWithBrand.expirationMessage && (
                            <div className={dealsModalsStyles.expirationMessage}>
                                <FontAwesomeIcon
                                    icon={faTimer}
                                    className={dealsModalsStyles.expirationMessageIcon}
                                />

                                {dealWithBrand.expirationMessage}
                            </div>
                        )}

                        <div className={dealsModalsStyles.dealTitle}>{dealWithBrand.title}</div>

                        {dealWithBrand.dealType === 'coupon' ? (
                            <>
                                <DealCouponButton
                                    onClick={clickOfferLink}
                                    couponCode={dealWithBrand!.couponCode || ''}
                                    showCode={thisDealIsLastClicked}
                                />

                                <div className={dealsModalsStyles.couponCopy}>
                                    Paste this coupon code at checkout to save online and earn{' '}
                                    {brand?.affiliateExclusionText ? 'up to ' : ''}
                                    {brand?.affiliateBonus
                                        ? `${brand?.affiliateBonus}%`
                                        : `${brand?.affiliateRebate}%`}{' '}
                                    when you make a purchase.
                                </div>
                            </>
                        ) : (
                            <Btn
                                className={dealsModalsStyles.shopButton}
                                disabled={isLoadingFlipgiveToken}
                                onClick={clickOfferLink}
                            >
                                Shop Deal
                                <ExternalLinkArrow
                                    className={dealsModalsStyles.externalLinkArrow}
                                    color={colors.white}
                                />
                            </Btn>
                        )}
                    </div>

                    <div className={dealsModalsStyles.detailsSection}>
                        <h4 className={dealsModalsStyles.sectionTitle}>
                            Earnings Details &amp; Exclusions
                        </h4>

                        <h5 className={dealsModalsStyles.sectionSubTitle}>Exclusions</h5>

                        <div className={dealsModalsStyles.sectionContent}>
                            Using loyalty or rewards points on your purchase will void your online
                            earnings.
                        </div>

                        {brand && brand.affiliateVariablePricingText?.length && (
                            <div
                                className={dealsModalsStyles.sectionContent}
                                dangerouslySetInnerHTML={{
                                    __html: brand.affiliateVariablePricingText,
                                }}
                            />
                        )}

                        <div className={dealsModalsStyles.sectionContent}>
                            When you buy through the link on RaiseRight, we may earn a commission.
                        </div>
                    </div>

                    {brand && brand.affiliateOfferTerms?.length && (
                        <div className={dealsModalsStyles.detailsSection}>
                            <h4 className={dealsModalsStyles.sectionTitle}>
                                Terms &amp; Conditions
                            </h4>

                            <div
                                className={dealsModalsStyles.sectionContent}
                                dangerouslySetInnerHTML={{ __html: brand.affiliateOfferTerms }}
                            />
                        </div>
                    )}
                </article>
            </Modal>

            {brand && brand?.imageUrl && (
                <AffiliateConfirmationModal
                    brandLogo={brand?.imageUrl}
                    earningsPercentage={`${brand?.affiliateVariablePricingText ? 'Up to ' : ''}${
                        brand?.isOnBonus && brand?.affiliateBonus
                            ? brand?.affiliateBonus
                            : brand?.affiliateRebate
                    }%`}
                    brandName={brand.name}
                    timeframeMessage={getConfirmationDrawerText(
                        brand?.averagePurchaseDelayHours || 0
                    )}
                    isOpen={confirmationModalShown}
                    onClose={() => {
                        onClose();
                        setConfirmationModalShown(false);
                    }}
                />
            )}
        </>
    );
};
